import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '@/contexts/ChatContext';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { CHAT_ROLE } from '@/utils/ChatUtils';
import { useParams, Navigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, MessageCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import ChatSidebar from '@/components/ChatSidebar';
import ConversationItem from '@/components/ConversationItem';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import type { Conversation } from '@/types/chat';

const Conversations = () => {
  const { user, loading: authLoading } = useAuth();
  const { isLoading: businessLoading } = useBusiness();
  const { toast } = useToast();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { 
    conversations, 
    loadingConversations, 
    loadConversation,
    loadAllConversations,
  } = useChat();
  
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedCounterpart, setSelectedCounterpart] = useState<{
    id: string;
    name: string;
    logo: string | null;
    userRole: string;
  } | null>(null);
  
  const [directConversationError, setDirectConversationError] = useState<string | null>(null);
  const [loadingDirectConversation, setLoadingDirectConversation] = useState(false);
  
  const conversationsLoadedRef = useRef(false);
  const userIdRef = useRef<string | null>(null);
  
  useEffect(() => {
    let isMounted = true;
    
    const loadConversationsOnce = async () => {
      if (user && isMounted && (!conversationsLoadedRef.current || userIdRef.current !== user.id) && !businessLoading) {
        try {
          console.log('[Conversations] Loading individual conversations for user:', user.id, 'businessLoading:', businessLoading);
          await loadAllConversations();
          conversationsLoadedRef.current = true;
          userIdRef.current = user.id;
        } catch (error) {
          console.error('[Conversations] Error loading conversations:', error);
          toast({
            title: "Error loading conversations",
            description: "There was a problem loading your conversations. Please try again.",
            variant: "destructive"
          });
        }
      } else if (businessLoading) {
        console.log('[Conversations] Waiting for business context to load before fetching conversations');
      }
    };
    
    loadConversationsOnce();
    
    return () => {
      isMounted = false;
    };
  }, [user, businessLoading, toast]);
  
  useEffect(() => {
    if (!user || !conversationId || loadingConversations || businessLoading) return;
    
    const openDirectConversation = async () => {
      console.log(`[Conversations] Attempting to open conversation directly from URL: ${conversationId}`);
      setLoadingDirectConversation(true);
      setDirectConversationError(null);
      
      try {
        const targetConversation = conversations.find(c => c.id === conversationId);
        
        if (targetConversation) {
          console.log(`[Conversations] Found conversation in list, opening: ${targetConversation.id}`);
          handleOpenChat(targetConversation);
          
          const element = document.getElementById(`conversation-${conversationId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
            setTimeout(() => {
              element.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50');
            }, 2000);
          }
        } else {
          console.log(`[Conversations] Conversation not found in list: ${conversationId}`);
          
          if (conversations.length > 0 && !loadingConversations) {
            setDirectConversationError("The conversation you're looking for was not found.");
            toast({
              title: "Conversation not found",
              description: "The conversation you're trying to access does not exist or you don't have permission to view it.",
              variant: "destructive"
            });
          } else {
            console.log('[Conversations] Skipping error message as conversations may still be loading');
          }
        }
      } catch (error) {
        console.error('[Conversations] Error opening direct conversation:', error);
        setDirectConversationError("There was an error loading this conversation.");
      } finally {
        setLoadingDirectConversation(false);
      }
    };
    
    openDirectConversation();
  }, [conversationId, conversations, user, loadingConversations, businessLoading, toast]);

  const handleOpenChat = (conversation: Conversation) => {
    console.log(`[Conversations] Opening chat for conversation ID: ${conversation.id}`);
    const isUserBusiness = conversation.member_id !== user?.id;
    
    if (chatOpen) {
      console.log('[Conversations] Closing existing chat to reset state');
      setChatOpen(false);
      
      setTimeout(() => {
        setCounterpartAndOpenChat();
      }, 300);
    } else {
      setCounterpartAndOpenChat();
    }
    
    function setCounterpartAndOpenChat() {
      if (isUserBusiness && conversation.member) {
        // Use username instead of full name
        const memberName = conversation.member.username || conversation.member.email || 'User';
          
        console.log(`[Conversations] Setting counterpart to member: ${memberName}`);
        setSelectedCounterpart({
          id: conversation.member_id,
          name: memberName,
          logo: conversation.member.avatar_url || null,
          userRole: CHAT_ROLE.BUSINESS_OWNER
        });
      } else {
        console.log(`[Conversations] Setting counterpart to business: ${conversation.business?.name || 'Business'}`);
        setSelectedCounterpart({
          id: conversation.business_id,
          name: conversation.business?.name || 'Business',
          logo: conversation.business?.company_logo || null,
          userRole: CHAT_ROLE.COMMUNITY_MEMBER
        });
      }
      
      console.log('[Conversations] Opening chat sidebar');
      setChatOpen(true);
      
      setTimeout(() => {
        console.log(`[Conversations] Loading conversation ID: ${conversation.id}`);
        loadConversation(conversation.id);
      }, 100);
    }
  };

  const renderConversationList = (conversationList: Conversation[]) => {
    if (conversationList.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <MessageCircle className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">No conversations yet</h3>
            <p className="text-muted-foreground max-w-md">
              Start a conversation with a business by visiting their service page and clicking on the chat button.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {conversationList.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            id={`conversation-${conversation.id}`}
            conversation={conversation}
            currentUserId={user?.id || ''}
            onSelect={handleOpenChat}
            isHighlighted={conversation.id === conversationId}
          />
        ))}
      </div>
    );
  };

  const renderError = () => {
    if (!directConversationError) return null;
    
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{directConversationError}</AlertDescription>
      </Alert>
    );
  };

  const isLoading = loadingConversations || loadingDirectConversation || businessLoading;
  
  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="container mx-auto py-8 md:py-8 px-4"
      >
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 lg:mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold">My Conversations</h1>
          </div>
          
          {renderError()}
          
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">
                {businessLoading ? "Loading business information..." : "Loading conversations..."}
              </p>
            </div>
          ) : (
            renderConversationList(conversations.filter(conv => conv.member_id === user?.id))
          )}
        </div>
      </motion.div>
      
      {selectedCounterpart && (
        <ChatSidebar
          open={chatOpen}
          onOpenChange={setChatOpen}
          counterpartId={selectedCounterpart.id}
          counterpartName={selectedCounterpart.name}
          counterpartLogo={selectedCounterpart.logo}
          userRole={selectedCounterpart.userRole}
        />
      )}
    </Layout>
  );
};

export default Conversations;
